import { useMemo } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { DataTransfer } from '@cast/shared-types';
import { aggregatorByTimestampFn, sumDataTransferCost } from '@cast/utils';

import { useWorkloadNetworkDataTransfer } from './useWorkloadNetworkDataTransfer';

dayjs.extend(utc);

export type NetworkCostChartPoint = {
  timestamp: string;
  chartTimestampUnix: number;
  egress: number;
  ingress: number;
};

export type NetworkCostChartTotals = {
  egress: number;
  ingress: number;
  total: number;
};

export const useNetworkCostChartData = () => {
  const { data, isLoading, isFetching, error, refetch, timeZone } =
    useWorkloadNetworkDataTransfer();

  const dataPoints: NetworkCostChartPoint[] = useMemo(() => {
    const item = data?.item;

    if (!item) {
      return [];
    }

    const byTimestamp: Record<string, DataTransfer> = {};

    item.egressMetrics?.forEach(
      aggregatorByTimestampFn.bind(null, byTimestamp, 'egress')
    );
    item.ingressMetrics?.forEach(
      aggregatorByTimestampFn.bind(null, byTimestamp, 'ingress')
    );

    return Object.keys(byTimestamp)
      .sort()
      .map((timestamp) => ({
        timestamp,
        chartTimestampUnix: dayjs.utc(timestamp).valueOf(),
        egress: sumDataTransferCost(byTimestamp[timestamp].egress),
        ingress: sumDataTransferCost(byTimestamp[timestamp].ingress),
      }));
  }, [data]);

  const totals = useMemo(() => {
    return dataPoints.reduce(
      (acc, point) => ({
        egress: acc.egress + point.egress,
        ingress: acc.ingress + point.ingress,
        total: acc.total + point.egress + point.ingress,
      }),
      { egress: 0, ingress: 0, total: 0 }
    );
  }, [dataPoints]);

  return {
    data: dataPoints,
    totals,
    isLoading,
    isFetching,
    error,
    refetch,
    timeZone,
  };
};
